import React from 'react';
import { Trophy, Edit3, Trash2, Scale, MessageSquare, AlertCircle } from 'lucide-react';
import {
  WRESTLING_STYLES,
  WRESTLING_CLASS_FORMATS,
  WRESTLING_MATCH_RESULTS,
  WRESTLING_HYDRATION,
  WRESTLING_CUT_METHODS,
} from '../../data/combativesWrestling';

/**
 * WrestlingSessionDetail — read view for a logged Wrestling session.
 * Mirrors BJJ/Judo session-detail structure with wrestling-specific blocks:
 * style badge, weight/hydration, coach feedback, match results.
 */
const WrestlingSessionDetail = ({ session, onClose, onEdit, onDelete }) => {
  if (!session) return null;

  const styleMeta = WRESTLING_STYLES.find((s) => s.id === session.style) || {};
  const classFormatLabel = WRESTLING_CLASS_FORMATS.find((c) => c.id === session.classFormat)?.label || session.classFormat;
  const techniques = session.techniques || [];
  const s = session.sparring || {};
  const w = session.weight || {};
  const coach = session.coachFeedback || [];
  const matches = session.matches || [];
  const injuries = session.injuryConcerns || [];

  const cutMethodLabel = WRESTLING_CUT_METHODS.find((c) => c.id === w.cutMethod)?.label;
  const hydrationMeta = WRESTLING_HYDRATION.find((h) => h.id === w.hydrationStatus);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-enf-surface-deep border border-enf-surface-border sm:rounded-xl flex flex-col max-h-[100vh] sm:max-h-[90vh]" data-testid="wrestling-session-detail">
        <div className="flex items-center justify-between p-5 border-b border-enf-surface-border flex-shrink-0">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-1 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Wrestling session
            </div>
            <div className="text-sm text-enf-text-secondary">
              {new Date(session.date).toLocaleDateString()} · {session.duration || '—'} min · {classFormatLabel || '—'}
              {styleMeta.label && <> · <span className="text-enf-role-info">{styleMeta.label}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {onEdit && (
              <button onClick={onEdit} aria-label="Edit" className="p-2 text-enf-text-muted hover:text-enf-text-primary" data-testid="wrestling-detail-edit">
                <Edit3 className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button onClick={onDelete} aria-label="Delete" className="p-2 text-enf-text-muted hover:text-enf-role-warning" data-testid="wrestling-detail-delete">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} aria-label="Close" className="p-2 text-enf-text-muted hover:text-enf-text-primary text-xl leading-none" data-testid="wrestling-detail-close">×</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
          {(session.focusModes || []).length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Focus modes</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {session.focusModes.map((m) => <span key={m} className="px-2 py-0.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs">{m}</span>)}
              </div>
            </div>
          )}
          {session.intent && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Intent</span><div className="text-enf-text-primary whitespace-pre-wrap">{session.intent}</div></div>
          )}

          {techniques.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Techniques · {techniques.length}</div>
              <div className="space-y-2">
                {techniques.map((t, i) => (
                  <div key={i} className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md" data-testid={`wrestling-detail-technique-${i}`}>
                    <div className="font-semibold text-enf-text-primary">{t.name || '(unnamed technique)'}</div>
                    <div className="text-xs text-enf-text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {t.phase && <span><span className="text-enf-text-muted">Phase:</span> <span className="text-enf-text-secondary capitalize">{t.phase}</span></span>}
                      {t.subgroup && <span><span className="text-enf-text-muted">Group:</span> <span className="text-enf-text-secondary">{t.subgroup}</span></span>}
                      {t.reps && <span><span className="text-enf-text-muted">Reps:</span> <span className="text-enf-text-secondary">{t.reps}</span></span>}
                      {t.mastery && <span className="text-enf-role-info">{t.mastery}</span>}
                    </div>
                    {t.note && <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap border-l-2 border-enf-surface-border pl-2">{t.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(s.liveGoCount || s.takedownsScored || s.takedownsConceded || s.escapesEarned || s.pinsScored) && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Going Live</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {s.liveGoCount && <div><span className="text-enf-text-muted">Live go&apos;s:</span> <span className="font-semibold">{s.liveGoCount}</span></div>}
                {s.partnersWorkedWith && <div><span className="text-enf-text-muted">Partners:</span> <span className="font-semibold">{s.partnersWorkedWith}</span></div>}
                {s.intensityRating && <div><span className="text-enf-text-muted">Intensity:</span> <span className="font-semibold">{s.intensityRating}/5</span></div>}
                {s.takedownsScored && <div><span className="text-enf-text-muted">TDs ✓:</span> <span className="font-semibold">{s.takedownsScored}</span></div>}
                {s.takedownsConceded && <div><span className="text-enf-text-muted">TDs ✗:</span> <span className="font-semibold">{s.takedownsConceded}</span></div>}
                {s.escapesEarned && <div><span className="text-enf-text-muted">Escapes ✓:</span> <span className="font-semibold">{s.escapesEarned}</span></div>}
                {s.escapesConceded && <div><span className="text-enf-text-muted">Escapes ✗:</span> <span className="font-semibold">{s.escapesConceded}</span></div>}
                {s.reversalsScored && <div><span className="text-enf-text-muted">Reversals ✓:</span> <span className="font-semibold">{s.reversalsScored}</span></div>}
                {s.reversalsConceded && <div><span className="text-enf-text-muted">Reversed ✗:</span> <span className="font-semibold">{s.reversalsConceded}</span></div>}
                {s.turnsScored && <div><span className="text-enf-text-muted">Turns ✓:</span> <span className="font-semibold">{s.turnsScored}</span></div>}
                {s.turnsConceded && <div><span className="text-enf-text-muted">Turns ✗:</span> <span className="font-semibold">{s.turnsConceded}</span></div>}
                {s.pinsScored && <div><span className="text-enf-text-muted">Pins ✓:</span> <span className="font-semibold">{s.pinsScored}</span></div>}
                {s.pinsConceded && <div><span className="text-enf-text-muted">Pinned ✗:</span> <span className="font-semibold">{s.pinsConceded}</span></div>}
                {s.ridingTimeSeconds && <div><span className="text-enf-text-muted">RT:</span> <span className="font-semibold">{s.ridingTimeSeconds}s</span></div>}
                {s.pushoutsScored && <div><span className="text-enf-text-muted">Push-outs ✓:</span> <span className="font-semibold">{s.pushoutsScored}</span></div>}
                {s.pushoutsConceded && <div><span className="text-enf-text-muted">Pushed ✗:</span> <span className="font-semibold">{s.pushoutsConceded}</span></div>}
              </div>
            </div>
          )}

          {coach.length > 0 && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Coach feedback</div>
              <div className="space-y-1.5">
                {coach.map((c, i) => (
                  <div key={i} className="text-xs">
                    {c.topic && <span className="px-1.5 py-0.5 bg-enf-surface-deep border border-enf-surface-border rounded text-[10px] mr-2 text-enf-role-info">{c.topic}</span>}
                    <span className="text-enf-text-secondary whitespace-pre-wrap">{c.note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {matches.length > 0 && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Matches · {matches.length}</div>
              <div className="space-y-2">
                {matches.map((m, i) => {
                  const resultMeta = WRESTLING_MATCH_RESULTS.find((r) => r.id === m.result);
                  const resultColor = m.result === 'win' ? 'text-enf-role-success' : m.result === 'loss' ? 'text-enf-accent-fg' : 'text-enf-text-muted';
                  return (
                    <div key={i} className="text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold uppercase tracking-wider ${resultColor}`}>{resultMeta?.label || m.result}</span>
                        {m.opponent && <span className="text-enf-text-primary">vs {m.opponent}</span>}
                        {m.score && <span className="text-enf-text-muted">· {m.score}</span>}
                      </div>
                      {m.notes && <div className="text-enf-text-secondary whitespace-pre-wrap pl-2 border-l-2 border-enf-surface-border">{m.notes}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(w.scaleWeightLb || w.hydrationStatus || w.targetClassLb) && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2 flex items-center gap-1.5"><Scale className="w-3 h-3" /> Weight & hydration</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {w.scaleWeightLb && <div><span className="text-enf-text-muted">Today:</span> <span className="font-semibold">{w.scaleWeightLb} lb</span></div>}
                {w.targetClassLb && <div><span className="text-enf-text-muted">Target:</span> <span className="font-semibold">{w.targetClassLb} lb</span></div>}
                {w.daysToWeighIn && <div><span className="text-enf-text-muted">Days out:</span> <span className="font-semibold">{w.daysToWeighIn}</span></div>}
                {cutMethodLabel && <div className="col-span-2"><span className="text-enf-text-muted">Method:</span> <span className="font-semibold">{cutMethodLabel}</span></div>}
                {hydrationMeta && (
                  <div className="col-span-2 sm:col-span-3">
                    <span className="text-enf-text-muted">Hydration:</span>{' '}
                    <span className={`font-semibold ${
                      hydrationMeta.id === 'red' ? 'text-enf-accent-fg' :
                      hydrationMeta.id === 'amber' ? 'text-enf-role-warning' :
                      'text-enf-role-success'
                    }`}>{hydrationMeta.label}</span>
                    <span className="text-enf-text-muted"> — {hydrationMeta.help}</span>
                  </div>
                )}
              </div>
              {w.notes && <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap border-l-2 border-enf-surface-border pl-2">{w.notes}</div>}
            </div>
          )}

          {injuries.length > 0 && (
            <div className="p-3 bg-enf-role-danger/10 border border-enf-role-danger/30 rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2 flex items-center gap-1.5"><AlertCircle className="w-3 h-3 text-enf-accent-fg" /> Injury concerns</div>
              <div className="space-y-1 text-xs">
                {injuries.map((j, i) => (
                  <div key={i}>
                    <span className="font-semibold text-enf-text-primary">{j.area || 'Area not noted'}</span>
                    {j.severity && <span className="text-enf-text-muted"> · severity {j.severity}/5</span>}
                    {j.note && <div className="text-enf-text-secondary mt-0.5 whitespace-pre-wrap">{j.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {session.reflection && (
            <div>
              <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Reflection</span>
              <div className="text-enf-text-secondary whitespace-pre-wrap">{session.reflection}</div>
            </div>
          )}

          {session.notes && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Notes</span><div className="text-enf-text-secondary whitespace-pre-wrap">{session.notes}</div></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WrestlingSessionDetail;
